import { useEffect, useMemo, useState } from 'react'
import {
  fetchChiNhanhOptionsDiaChiLayHang,
  type DiaChiLayHangChiNhanhOption,
} from '@/apis/diaChiLayHangApi'

export type KhoVatLyOption = { value: string; label: string }

/** Bỏ tiền tố «Kho» trên nhãn item (vd. «Kho Hà Nội» → «Hà Nội»). */
function stripKhoPrefix(raw: string): string {
  const s = raw.trim()
  if (!s) return s
  return s.replace(/^kho\s+/i, '').trim() || s
}

/** Options kho vật lý (unique theo `khoVatLyId`) từ chi nhánh dự án hiện tại. */
export function useKhoVatLyOptions(): {
  options: KhoVatLyOption[]
  chiNhanhRows: DiaChiLayHangChiNhanhOption[]
  loading: boolean
} {
  const [chiNhanhRows, setChiNhanhRows] = useState<DiaChiLayHangChiNhanhOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchChiNhanhOptionsDiaChiLayHang()
      .then((rows) => {
        if (!cancelled) setChiNhanhRows(rows)
      })
      .catch(() => {
        if (!cancelled) setChiNhanhRows([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const options = useMemo(() => {
    const byId = new Map<string, string>()
    for (const cn of chiNhanhRows) {
      if (cn.khoVatLyId == null || cn.khoVatLyId <= 0) continue
      const key = String(cn.khoVatLyId)
      if (byId.has(key)) continue
      const raw = cn.khoVatLyTen || cn.khoVatLyMa || `#${cn.khoVatLyId}`
      byId.set(key, stripKhoPrefix(raw))
    }
    return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
      a.label.localeCompare(b.label, 'vi'),
    )
  }, [chiNhanhRows])

  return { options, chiNhanhRows, loading }
}
